/**
 * Permissions API Service
 *
 * Handles all permission-related API calls for the granular permission system:
 * - Get all available permissions
 * - Get user's effective permissions
 * - Get user's permission overrides (grants/revokes)
 * - Grant permissions to users
 * - Revoke permissions from users
 * - Remove permission overrides
 */

import {post} from './client';
import CryptoJS from "crypto-js";
import {KEY} from "../../Constants";

// Helper function to get decrypted data
const getOriginalText = () => {
    const encryptedData = localStorage.getItem("myMap");
    return encryptedData
        ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
        : "";
};

export const permissionsApi = {
    /**
     * Get all available permissions in the system (55 permissions)
     * @param {Object} data
     * @param {number} data.landlord_id - Landlord ID
     * @returns {Promise<Object>} List of all permissions grouped by category
     */
    getAll: async (data) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/permissions/get', data,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Get user's effective permissions (role + grants - revokes)
     * @param {Object} data
     * @param {number} data.landlord_id - Landlord ID
     * @param {number} data.sub_user_id - Sub-user ID
     * @param {number} [data.property_id] - Optional property ID filter
     * @returns {Promise<Object>} User's current permissions with source info
     */
    getEffective: async (data) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/permissions/effective', data,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Get user's custom permission overrides (grants and revokes only)
     * @param {Object} data
     * @param {number} data.landlord_id - Landlord ID
     * @param {number} data.sub_user_id - Sub-user ID
     * @param {number} [data.property_id] - Optional property ID filter
     * @returns {Promise<Object>} User's grants and revokes
     */
    getOverrides: async (data) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/permissions/overrides', data,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Grant a permission to a user (beyond their role)
     * @param {Object} data
     * @param {number} data.landlord_id - Landlord ID
     * @param {number} data.sub_user_id - Sub-user ID
     * @param {string} data.permission_name - Permission name to grant
     * @param {number|null} data.property_id - Property ID or null for all properties
     * @param {string} [data.notes] - Optional notes about the grant
     * @returns {Promise<Object>} Grant response
     */
    grant: async (data) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/permissions/grant', data,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Revoke a permission from a user (that their role normally has)
     * @param {Object} data
     * @param {number} data.landlord_id - Landlord ID
     * @param {number} data.sub_user_id - Sub-user ID
     * @param {string} data.permission_name - Permission name to revoke
     * @param {number|null} data.property_id - Property ID or null for all properties
     * @param {string} [data.notes] - Optional notes about the revoke
     * @returns {Promise<Object>} Revoke response
     */
    revoke: async (data) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/permissions/revoke', data,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },

    /**
     * Remove a permission override (revert to role default)
     * @param {Object} data
     * @param {number} data.landlord_id - Landlord ID
     * @param {number} data.sub_user_id - Sub-user ID
     * @param {string} data.permission_name - Permission name
     * @param {number|null} data.property_id - Property ID (must match original override)
     * @returns {Promise<Object>} Remove response
     */
    removeOverride: async (data) => {
        const originalText = getOriginalText();

        return post('pegasus/visionary/userManagement/permissions/remove-override', data,
            {
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                }
            });
    },
};

export default permissionsApi;
